// Leaving the segment builder with conditions that are not saved asks first
// (UI-4): the browser's own question for a closed tab or a reload, and a
// dialog for a navigation inside the admin — the router's navigator is
// wrapped while there is unsaved work, the same way the template editor does
// it (the admin runs on <BrowserRouter>, so `useBlocker` is not available).

import { useCallback, useContext, useEffect, useRef, useState } from "react";
import type { MutableRefObject, ReactNode } from "react";
import { UNSAFE_NavigationContext } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

/** `bypass`: while true, a navigation is the page's own (to the saved
 * segment after a save), not somebody leaving. */
export function useLeaveGuard(dirty: boolean, bypass: MutableRefObject<boolean>): ReactNode {
  const { navigator } = useContext(UNSAFE_NavigationContext);
  const [pending, setPending] = useState<(() => void) | null>(null);
  const dirtyRef = useRef(dirty);
  dirtyRef.current = dirty;

  const confirm = useCallback((proceed: () => void) => {
    if (!dirtyRef.current) proceed();
    else setPending(() => proceed);
  }, []);

  useEffect(() => {
    if (!dirty) return undefined;
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    const push = navigator.push;
    const replace = navigator.replace;
    navigator.push = (...args: Parameters<typeof push>) =>
      bypass.current ? push.apply(navigator, args) : confirm(() => push.apply(navigator, args));
    navigator.replace = (...args: Parameters<typeof replace>) =>
      bypass.current ? replace.apply(navigator, args) : confirm(() => replace.apply(navigator, args));
    return () => {
      window.removeEventListener("beforeunload", onBeforeUnload);
      navigator.push = push;
      navigator.replace = replace;
    };
  }, [dirty, navigator, confirm, bypass]);

  return (
    <Dialog open={pending !== null} onOpenChange={(open) => !open && setPending(null)}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Nem mentett feltételek</DialogTitle>
          <DialogDescription>
            A szegmensben olyan változás van, amelyet még nem mentettél. Ha most továbblépsz, elveszik.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" type="button" onClick={() => setPending(null)}>
            Maradok
          </Button>
          <Button
            variant="outline-danger"
            type="button"
            onClick={() => {
              const proceed = pending;
              dirtyRef.current = false;
              setPending(null);
              proceed?.();
            }}
          >
            Elvetés és tovább
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
